import type { FC, ReactNode } from 'react';
import React, { useMemo } from 'react';

import { EXTERNAL_SHARE, ACCESS_LINK } from '@confluence/named-routes/entry-points/routes.ts';
import { expVal } from '@confluence/feature-experiments';

import { useWithUserTypes } from './useWithUserTypes.ts';
import { ExternalShareContext } from './ExternalShareContext.tsx';

type ExternalShareContextProviderProps = {
	path: string;
	children?: ReactNode;
	shareToken?: string;
};

export const ExternalShareContextProvider: FC<ExternalShareContextProviderProps> = ({
	children,
	path,
	shareToken,
}) => {
	const isExternalShareRequest = !!EXTERNAL_SHARE.match(path);
	const isAccessLinkRequest =
		!!ACCESS_LINK.match(path) && expVal('shareable_links_m0', 'isEnabled', false);
	const { userType } = useWithUserTypes(isExternalShareRequest || isAccessLinkRequest);

	// Extract shareToken from path if it's an external share request
	const extractedShareToken = useMemo(() => {
		if (isExternalShareRequest) {
			const match = EXTERNAL_SHARE.match(path);
			return match?.params?.externalToken || undefined;
		} else if (isAccessLinkRequest) {
			const match = ACCESS_LINK.match(path);
			return match?.params?.shareToken || undefined;
		}
		return undefined;
	}, [isExternalShareRequest, path, isAccessLinkRequest]);

	// Use provided shareToken if available, otherwise use extracted one
	const resolvedShareToken = shareToken || extractedShareToken;

	const externalShareContextValue = useMemo(
		() => ({
			isExternalShareRequest: isExternalShareRequest || isAccessLinkRequest,
			userType,
			shareToken: resolvedShareToken,
			isAccessLinkRequest,
		}),
		[isExternalShareRequest, userType, resolvedShareToken, isAccessLinkRequest],
	);

	return (
		<ExternalShareContext.Provider value={externalShareContextValue}>
			{children}
		</ExternalShareContext.Provider>
	);
};
